import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import { BRAND } from './auditTones';

/**
 * The mapping step: what each column in the uploaded file was taken to mean,
 * and the chance to correct it before the audit runs.
 *
 * This is what makes the audit work on any vendor's export rather than only on
 * iCentrix's. The server auto-detects from the header text; anything it gets
 * wrong - or an export using wording nobody anticipated - is fixed here, and
 * the corrections travel with the run.
 *
 * Most rows are correctly detected, so the detected state is deliberately quiet
 * and only the exceptions carry colour. Repeating a green "detected
 * automatically" down every row buries the two rows that actually need a
 * decision, which is the opposite of what this screen is for.
 */

const NO_COMPARE = '__none__';

const KIND_LABEL = {
  identity: 'Identifies the person',
  compare: 'Compared against MapYourMeds',
  unsupported: 'No field in MapYourMeds'
};

// A dot reads as a category marker at a glance; the "=" / "-" chips it replaces
// looked like content rather than metadata.
const KIND_DOT = {
  identity: '#27bcfd',
  compare: '#00d27a',
  unsupported: '#b6bfcc',
  none: '#d8e2ef'
};

const Dot = ({ kind }) => (
  <span
    className="d-inline-block rounded-circle flex-shrink-0"
    style={{ width: 8, height: 8, backgroundColor: KIND_DOT[kind] || KIND_DOT.none }}
  />
);

Dot.propTypes = { kind: PropTypes.string };

const ColumnMapping = ({ preview, fields, overrides, onChange, onReset, disabled }) => {
  const options = useMemo(() => {
    const group = kind => ({
      label: KIND_LABEL[kind],
      options: fields.filter(f => f.kind === kind).map(f => ({ value: f.id, label: f.label, kind }))
    });
    return [
      { label: 'Ignore', options: [{ value: NO_COMPARE, label: 'Do not compare', kind: 'none' }] },
      group('identity'),
      group('compare'),
      group('unsupported')
    ];
  }, [fields]);

  const flatOptions = useMemo(() => options.reduce((all, g) => all.concat(g.options), []), [options]);

  const valueFor = index => {
    const fieldId = preview.byIndex[index];
    return flatOptions.find(o => o.value === (fieldId || NO_COMPARE)) || flatOptions[0];
  };

  const samplesFor = index => {
    const vals = (preview.sampleRows || [])
      .map(r => (r[index] === undefined ? '' : String(r[index]).trim()))
      .filter(Boolean)
      .slice(0, 2);
    return vals.length ? vals.join('  ·  ') : <span className="text-500">(empty)</span>;
  };

  // How this row reads: the default is quiet, the exceptions are not.
  const stateOf = index => {
    const dup = (preview.duplicates || []).find(d => d.index === index);
    if (dup) {
      return {
        tone: 'warn',
        node: (
          <span className="text-warning">
            <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
            Already taken by column {dup.takenBy + 1}
          </span>
        )
      };
    }

    const unsupported = (preview.unsupported || []).find(u => u.index === index);
    if (unsupported) {
      return { tone: 'muted', node: <span className="text-500 fs-11">{unsupported.why}</span> };
    }

    const unmapped = (preview.unmapped || []).find(u => u.index === index);
    if (unmapped) {
      return unmapped.reason === 'excluded by the reviewer'
        ? { tone: 'muted', node: <Badge bg="secondary">Excluded</Badge> }
        : {
            tone: 'warn',
            node: (
              <span className="text-warning">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                Not recognised — pick a field or leave it out
              </span>
            )
          };
    }

    return {
      tone: 'ok',
      node: (
        <span className="text-500">
          <FontAwesomeIcon icon="check" className="me-1" />
          Detected
        </span>
      )
    };
  };

  const overrideCount = Object.keys(overrides || {}).length;
  const comparedCount = Object.keys(preview.mapping || {}).length;
  const noFieldCount = (preview.unsupported || []).length;
  const unknownCount = (preview.unmapped || []).filter(u => u.reason !== 'excluded by the reviewer').length;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="fw-semibold">Column mapping</span>
          <Badge bg="success" className="fw-medium">
            {comparedCount} compared
          </Badge>
          {noFieldCount > 0 && (
            <Badge bg="secondary" className="fw-medium">
              {noFieldCount} no field with us
            </Badge>
          )}
          {unknownCount > 0 && (
            <Badge bg="warning" className="fw-medium">
              {unknownCount} not recognised
            </Badge>
          )}
          {overrideCount > 0 && (
            <Badge bg="info" className="fw-medium">
              {overrideCount} corrected
            </Badge>
          )}
        </div>
        {overrideCount > 0 && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            onClick={onReset}
            disabled={disabled}
          >
            <FontAwesomeIcon icon="sync-alt" className="me-1" />
            Reset to auto-detected
          </Button>
        )}
      </div>

      {preview.missingRequired && preview.missingRequired.length > 0 && (
        <Alert variant="danger" className="py-2 d-flex align-items-start">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2 mt-1 flex-shrink-0" />
          <div className="fs-10">
            This file cannot be audited yet — it needs {preview.missingRequired.join(', ')}. Map the
            right columns below.
          </div>
        </Alert>
      )}

      {(preview.duplicates || []).length > 0 && (
        <Alert variant="warning" className="py-2 fs-10 d-flex align-items-start">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2 mt-1 flex-shrink-0" />
          <div>Two columns claim the same field. Only the first is used — change one of them below.</div>
        </Alert>
      )}

      <div className="table-responsive rounded-3 border">
        <Table size="sm" hover className="mb-0 align-middle">
          <thead>
            <tr className="bg-body-tertiary">
              <th className="fs-11 text-uppercase text-600 fw-medium py-2" style={{ width: 48 }}>
                #
              </th>
              <th className="fs-11 text-uppercase text-600 fw-medium py-2" style={{ width: '21%' }}>
                Column in your file
              </th>
              <th className="fs-11 text-uppercase text-600 fw-medium py-2" style={{ width: '25%' }}>
                Example values
              </th>
              <th className="fs-11 text-uppercase text-600 fw-medium py-2" style={{ width: '24%' }}>
                Compared as
              </th>
              <th className="fs-11 text-uppercase text-600 fw-medium py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {preview.headers.map((header, index) => {
              const current = valueFor(index);
              const state = stateOf(index);
              const recedes = state.tone === 'muted';
              return (
                <tr
                  key={index}
                  style={{
                    backgroundColor: state.tone === 'warn' ? 'rgba(245,128,62,0.05)' : undefined,
                    opacity: recedes ? 0.7 : 1
                  }}
                >
                  <td className="text-500 fs-10">{index + 1}</td>
                  <td>
                    <span className="fw-semibold fs-10">
                      {header || <em className="text-500 fw-normal">(no header)</em>}
                    </span>
                  </td>
                  <td className="fs-11 text-600 text-truncate" style={{ maxWidth: 0 }}>
                    {samplesFor(index)}
                  </td>
                  <td>
                    <div style={{ fontSize: '0.8rem' }}>
                      <ThemedSelect
                        options={options}
                        value={current}
                        isDisabled={disabled}
                        menuPortalTarget={document.body}
                        onChange={opt => onChange(index, opt && opt.value !== NO_COMPARE ? opt.value : null)}
                        // The menu is portalled to document.body to stop the
                        // table's overflow clipping it, which also puts it
                        // outside the wrapper below - so it does not inherit
                        // that font size. Sizing the rendered labels is what
                        // reaches it, since ThemedSelect applies its own
                        // `styles` after props and discards any we pass.
                        formatOptionLabel={opt => (
                          <span
                            className="d-flex align-items-center gap-2"
                            style={{ fontSize: '0.8rem' }}
                          >
                            <Dot kind={opt.kind} />
                            <span className={opt.kind === 'none' ? 'text-500' : ''}>{opt.label}</span>
                          </span>
                        )}
                        formatGroupLabel={group => (
                          <span style={{ fontSize: '0.68rem', letterSpacing: '0.03em' }}>
                            {group.label}
                          </span>
                        )}
                      />
                    </div>
                  </td>
                  <td className="fs-10">{state.node}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-3 mt-2 fs-11 text-600">
        <span>Key</span>
        {[
          { kind: 'identity', label: 'Identifies the person' },
          { kind: 'compare', label: 'Compared' },
          { kind: 'unsupported', label: 'No field in MapYourMeds' },
          { kind: 'none', label: 'Not compared' }
        ].map(k => (
          <span key={k.kind} className="d-inline-flex align-items-center gap-1">
            <Dot kind={k.kind} />
            {k.label}
          </span>
        ))}
        <span className="ms-auto" style={{ color: BRAND.base }}>
          Change any row to correct it — the audit uses what is shown here
        </span>
      </div>
    </>
  );
};

ColumnMapping.propTypes = {
  preview: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  overrides: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default ColumnMapping;
